import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import Accordion from "@material-ui/core/Accordion";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";

import { i18n } from "../../translate/i18n";
import { getTokens } from "../../styles/tokens";
import { StatusBadge } from "../../components/ui";
import { Card, SectionHead, MethodBadge, Code, useUi } from "./ui";
import CodeBlock from "./CodeBlock";
import { BASE_URL } from "./snippets";

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    list: { display: "flex", flexDirection: "column", gap: 8 },
    acc: {
      backgroundColor: t.paper,
      border: `1px solid ${t.line}`,
      borderRadius: `${t.radius.sm}px !important`,
      boxShadow: "none",
      "&:before": { display: "none" },
      "&.Mui-expanded": { margin: 0, borderColor: t.tint.strong },
      overflow: "hidden",
    },
    summary: {
      minHeight: 48,
      padding: "0 16px",
      "&.Mui-expanded": { minHeight: 48, borderBottom: `1px solid ${t.line}` },
      "& .MuiAccordionSummary-content": {
        margin: "10px 0",
        display: "flex",
        alignItems: "center",
        gap: 10,
        minWidth: 0,
        flexWrap: "wrap",
      },
      "& .MuiAccordionSummary-content.Mui-expanded": { margin: "10px 0" },
      "& .MuiAccordionSummary-expandIcon": { color: t.textMuted },
    },
    sumTitle: {
      fontFamily: t.font,
      fontSize: 14,
      fontWeight: 600,
      color: t.text,
      minWidth: 0,
    },
    sumDesc: {
      fontFamily: t.font,
      fontSize: 12,
      color: t.textMuted,
      minWidth: 0,
      flexBasis: "100%",
      [theme.breakpoints.up("sm")]: { flexBasis: "auto", marginLeft: "auto" },
    },
    details: {
      display: "flex",
      flexDirection: "column",
      gap: 14,
      padding: "16px",
      backgroundColor: t.mode === "dark" ? t.paper : t.muted,
    },
    p: { fontFamily: t.font, fontSize: 13, lineHeight: 1.6, color: t.textSecondary, margin: 0 },
    tableWrap: {
      overflowX: "auto",
      border: `1px solid ${t.line}`,
      borderRadius: t.radius.sm,
      backgroundColor: t.paper,
    },
    // cabeçalho e linhas vêm do tema; aqui só o que é próprio de doc (texto 13, topo)
    table: {
      minWidth: 520,
      "& th": { whiteSpace: "nowrap" },
      "& td": { fontSize: 13, verticalAlign: "top", lineHeight: 1.5 },
      "& tbody tr:last-child td": { borderBottom: "none" },
    },
    req: { fontFamily: t.font, fontSize: 11, fontWeight: 600, color: t.brand },
    opt: { fontFamily: t.font, fontSize: 11, color: t.textMuted },
    two: {
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr)",
      gap: 12,
      [theme.breakpoints.up("md")]: { gridTemplateColumns: "repeat(2, minmax(0, 1fr))" },
    },
  };
});

const P = ({ children }) => {
  const c = useStyles();
  return <p className={c.p}>{children}</p>;
};

const ParamTable = ({ rows }) => {
  const c = useStyles();
  return (
    <div className={c.tableWrap}>
      <Table size="small" className={c.table}>
        <TableHead>
          <TableRow>
            {/* TODO i18n */}
            <TableCell>Campo</TableCell>
            <TableCell>Tipo</TableCell>
            <TableCell>Descrição</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.name}>
              <TableCell>
                <Code>{r.name}</Code>{" "}
                {r.required ? <span className={c.req}>obrigatório</span> : <span className={c.opt}>opcional</span>}
              </TableCell>
              <TableCell style={{ whiteSpace: "nowrap" }}>{r.type}</TableCell>
              <TableCell>{r.desc}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

const Item = ({ method, path, title, desc, children, defaultExpanded }) => {
  const c = useStyles();
  const ui = useUi();
  return (
    <Accordion className={c.acc} defaultExpanded={defaultExpanded} TransitionProps={{ timeout: 180 }}>
      <AccordionSummary className={c.summary} expandIcon={<ExpandMoreIcon />}>
        {method && <MethodBadge method={method} />}
        {path ? <span className={ui.path}>{path}</span> : <span className={c.sumTitle}>{title}</span>}
        {path && title && <span className={c.sumDesc}>{title}</span>}
        {!path && desc && <span className={c.sumDesc}>{desc}</span>}
      </AccordionSummary>
      <AccordionDetails className={c.details}>{children}</AccordionDetails>
    </Accordion>
  );
};

const NUMBER_ROW = {
  name: "number",
  type: "string",
  required: true,
  desc: "Só dígitos: código do país + DDD + número (ex.: 5511999999999). Para grupos, o ID do grupo.",
};

/**
 * Bloco 3 — referência: somente o que existe em backend/src/routes/apiRoutes.ts
 * (send, send/linkImage, checkNumber). Não há webhook de retorno para a API externa.
 */
const Reference = () => {
  const c = useStyles();
  const ui = useUi();

  return (
    <Card id="api-reference">
      <SectionHead
        step={3}
        // TODO i18n
        title="Referência"
        description={`Base: ${BASE_URL}. Todas as rotas são POST e exigem o cabeçalho Authorization: Bearer <token da conexão>.`}
      />

      <div className={c.list}>
        <Item title="Autenticação e formato do número" desc="Como toda requisição é validada">
          <P>
            O token identifica a <b>conexão</b> que vai enviar (e a empresa). Envie sempre{" "}
            <Code>Authorization: Bearer &lt;token&gt;</Code>. Para JSON, use{" "}
            <Code>Content-Type: application/json</Code>; para arquivo, <Code>multipart/form-data</Code>.
          </P>
          <P>{i18n.t("messagesAPI.API.instructions.comments2")}</P>
          <div className={ui.hint}>
            {i18n.t("messagesAPI.API.instructions.codeCountry")} · {i18n.t("messagesAPI.API.instructions.code")} ·{" "}
            {i18n.t("messagesAPI.API.instructions.number")} — ex.: <Code>5511999999999</Code>
          </div>
        </Item>

        <Item method="POST" path="/api/messages/send" title="Texto ou arquivo" defaultExpanded>
          <P>
            Envia uma mensagem de texto (JSON) ou um arquivo (multipart, campo <Code>medias</Code>). Cria o
            contato e o ticket se não existirem. Mensagens de texto entram na fila de transmissão e são
            enviadas pelo worker; arquivos são enviados na hora.
          </P>
          <ParamTable
            rows={[
              NUMBER_ROW,
              { name: "body", type: "string", required: false, desc: "Texto da mensagem (ou legenda do arquivo). Para arquivo, padrão = nome do arquivo." },
              { name: "medias", type: "file", required: false, desc: "Arquivo (multipart/form-data). PDF, imagem, vídeo, áudio ou outro." },
              { name: "userId", type: "number", required: false, desc: "ID do atendente que assume o ticket (abre o ticket para ele)." },
              { name: "queueId", type: "number", required: false, desc: "ID da fila do ticket." },
              { name: "sendSignature", type: "boolean", required: false, desc: `${i18n.t("messagesAPIInstructions.singMessage")} — prefixa *Nome do atendente:* (precisa de userId).` },
              { name: "closeTicket", type: "boolean", required: false, desc: `${i18n.t("messagesAPIInstructions.closeTicket")} — encerra o ticket logo após o envio.` },
              { name: "noRegister", type: "boolean", required: false, desc: "Envia direto pelo WhatsApp sem criar ticket nem registrar a mensagem no atendimento." },
              { name: "schedule", type: "date ISO", required: false, desc: "Agenda o envio (só texto). Sem informar, entra na próxima vaga da fila." },
            ]}
          />
          <div className={c.two}>
            <CodeBlock
              title="Resposta 200 (texto — enfileirada)"
              code={JSON.stringify(
                { message: "Mensagem enviada a fila de transmissão com sucesso", companyId: 1, schedule: "2026-08-30T12:00:00.000Z", filaId: 123 },
                null,
                2
              )}
              wrap
            />
            <CodeBlock title="Resposta 200 (arquivo)" code={JSON.stringify({ status: "SUCCESS" }, null, 2)} />
          </div>
        </Item>

        <Item method="POST" path="/api/messages/send/linkImage" title="Imagem por URL">
          <P>
            Baixa a imagem da URL informada e envia como mídia com legenda opcional. O ticket criado é{" "}
            <b>encerrado automaticamente</b> após o envio.
          </P>
          <ParamTable
            rows={[
              NUMBER_ROW,
              { name: "url", type: "string", required: true, desc: "URL pública da imagem (jpg, png, gif)." },
              { name: "caption", type: "string", required: false, desc: "Legenda da imagem." },
              { name: "msdelay", type: "number", required: false, desc: "Atraso em milissegundos antes do envio (conexões Baileys)." },
            ]}
          />
          <CodeBlock title="Resposta 200" code={JSON.stringify({ status: "SUCCESS" }, null, 2)} />
        </Item>

        <Item method="POST" path="/api/messages/checkNumber" title="Verificar número">
          <P>
            Consulta se o número tem WhatsApp, usando a conexão padrão da empresa. Devolve o número
            formatado (JID) quando existe.
          </P>
          <ParamTable rows={[NUMBER_ROW]} />
          <div className={c.two}>
            <CodeBlock
              title="Resposta 200"
              code={JSON.stringify({ existsInWhatsapp: true, number: "5511999999999", numberFormatted: "5511999999999@s.whatsapp.net" }, null, 2)}
              wrap
            />
            <CodeBlock
              title="Resposta 400"
              code={JSON.stringify({ existsInWhatsapp: false, number: "5511999999999@s.whatsapp.net", error: "Not exists on Whatsapp" }, null, 2)}
              wrap
            />
          </div>
        </Item>

        <Item title="Códigos de erro" desc="O que cada status significa">
          <div className={c.tableWrap}>
            <Table size="small" className={c.table}>
              <TableHead>
                <TableRow>
                  <TableCell>Status</TableCell>
                  <TableCell>Corpo</TableCell>
                  <TableCell>Causa</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell><StatusBadge color="error" size="sm" label="401" /></TableCell>
                  <TableCell><Code>{`{ "error": "ERR_SESSION_EXPIRED" }`}</Code></TableCell>
                  <TableCell>Cabeçalho <Code>Authorization</Code> ausente.</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><StatusBadge color="error" size="sm" label="403" /></TableCell>
                  <TableCell><Code>{`{ "error": "Invalid token. ..." }`}</Code></TableCell>
                  <TableCell>Token não corresponde a nenhuma conexão.</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><StatusBadge color="warning" size="sm" label="400" /></TableCell>
                  <TableCell><Code>{`{ "error": "Invalid number format. Only numbers is allowed." }`}</Code></TableCell>
                  <TableCell><Code>number</Code> vazio ou com caracteres que não são dígitos.</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><StatusBadge color="warning" size="sm" label="400" /></TableCell>
                  <TableCell><Code>{`{ "error": "Error sending API media: ..." }`}</Code></TableCell>
                  <TableCell>Falha ao enviar o arquivo/imagem (conexão desconectada, arquivo inválido, URL inacessível).</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><StatusBadge color="warning" size="sm" label="400" /></TableCell>
                  <TableCell><Code>{`{ "existsInWhatsapp": false, ... }`}</Code></TableCell>
                  <TableCell>Em <Code>/checkNumber</Code>: número não tem WhatsApp.</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><StatusBadge color="neutral" size="sm" label="500" /></TableCell>
                  <TableCell><Code>{`{ "error": "Internal server error" }`}</Code></TableCell>
                  <TableCell>Erro inesperado no servidor.</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </Item>

        <Item title="Limites e fila de envio" desc="Ritmo, contagem de uso e permissão">
          <P>
            <b>Fila de texto:</b> cada mensagem de texto recebe um horário de envio com{" "}
            <b>~60 s de intervalo</b> em relação à última agendada da empresa (ou o <Code>schedule</Code>{" "}
            informado). O worker processa a fila a cada minuto, por conexão. Conexões WhatsApp Oficial (Meta)
            seguem o fluxo próprio da API oficial.
          </P>
          <P>
            <b>Uso diário:</b> cada chamada é contabilizada por empresa e por tipo (texto, PDF, imagem, vídeo,
            outros, verificação de número).
          </P>
          <P>
            <b>Permissão:</b> a API externa só fica disponível para planos com <Code>useExternalApi</Code>{" "}
            ativo. Gere/renove o token editando a conexão em <b>Conexões</b>.
          </P>
        </Item>
      </div>
    </Card>
  );
};

export default Reference;
